import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

import EmailInput from './ui/EmailInput';
import PhoneInput from './ui/PhoneInput';
import { Textarea } from './ui/textarea';
import { DatePickerFr } from './ui/DatePickerFr';
import SelecteurActivites from './SelecteurActivites';
import { Edit3, FilePlus, Save, AlertCircle, Building2, FileText, Info, Loader2, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SouscriptionModificationDialog({ 
  isOpen, 
  onClose, 
  souscription, 
  getAuthHeader,
  onSuccess,
  mode = 'edit' // 'edit' ou 'avenant'
}) {
  const [loading, setLoading] = useState(false);
  const [checkingModifiable, setCheckingModifiable] = useState(true);
  const [canModify, setCanModify] = useState(false);
  const [canCreateAvenant, setCanCreateAvenant] = useState(false);
  const [activeTab, setActiveTab] = useState(mode === 'avenant' ? 'avenant' : 'modification');

  // Calcul BATICO en direct (montant estimé recalculé)
  const [liveCalc, setLiveCalc] = useState(null);
  const [liveCalcLoading, setLiveCalcLoading] = useState(false);
  const [liveCalcError, setLiveCalcError] = useState(null);
  
  // Form data pour modification
  const [editForm, setEditForm] = useState({
    raison_sociale: '',
    siret: '',
    nom_gerant: '',
    prenom_gerant: '',
    adresse_entreprise: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    chiffre_affaires: 0,
    montant_ht_contribution: 0,
    montant_total: 0,
    effectif: '',
    date_debut: '',
    date_fin: '',
    notes: '',
    activites_selectionnees: {}
  });
  
  // Form data pour avenant
  const [avenantForm, _setAvenantForm] = useState({
    montant_ht_contribution: 0,
    motif: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && souscription) {
      checkModifiable();
      populateEditForm();
    }
  }, [isOpen, souscription]);

  // Recalcul BATICO en direct quand le CA ou les activités changent
  const activitesKey = JSON.stringify(editForm.activites_selectionnees);
  useEffect(() => {
    if (!isOpen || activeTab !== 'modification') return;
    const ca = parseFloat(editForm.chiffre_affaires) || 0;
    const sel = editForm.activites_selectionnees || {};
    const go = sel.gros_oeuvre || [];
    const nd = sel.second_oeuvre || [];
    const tp = sel.travaux_publics || [];
    const pib = sel.pib || [];
    if (ca <= 0 || (go.length + nd.length + tp.length + pib.length) === 0) {
      setLiveCalc(null);
      setLiveCalcError(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLiveCalcLoading(true);
      setLiveCalcError(null);
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/calculateur/batico`, {
          ca_ht: ca,
          activites_ids_go: go,
          activites_ids_2nd: nd,
          activites_ids_tp: tp,
          activites_ids_pib: pib,
        });
        setLiveCalc({
          montant_ht: data.montant_ht,
          montant_ttc: data.montant_ttc,
          plancher: data.plancher_contributif,
        });
      } catch (e) {
        setLiveCalcError(e?.response?.data?.detail || 'Calcul indisponible');
        setLiveCalc(null);
      } finally {
        setLiveCalcLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [isOpen, activeTab, editForm.chiffre_affaires, activitesKey]);

  const checkModifiable = async () => {
    if (!souscription) return;
    
    setCheckingModifiable(true);
    
    // Vérification basée sur les données de la souscription
    const isPaid = souscription.paiement_valide === true;
    const statut = souscription.statut || '';
    // Statuts terminaux : jamais modifiables (même non payés)
    const statutsTermines = ['annulee', 'annule', 'archivee', 'archive', 'rejetee', 'rejete', 'refusee', 'refuse', 'expiree', 'expire'];

    // Peut modifier tant que NON payé et statut non terminal (inclut désormais « signee »)
    const modifiable = !isPaid && !statutsTermines.includes(statut);
    // Peut créer avenant si validée et payée
    const avenantPossible = isPaid && statut === 'validee';
    
    setCanModify(modifiable);
    setCanCreateAvenant(avenantPossible);
    
    // Si on ne peut pas modifier mais on peut créer un avenant, changer l'onglet
    if (!modifiable && avenantPossible) {
      setActiveTab('avenant');
    }
    
    setCheckingModifiable(false);
  };

  const populateEditForm = () => {
    if (!souscription) return;
    
    setEditForm({
      raison_sociale: souscription.raison_sociale || '',
      siret: souscription.siret || '',
      nom_gerant: souscription.nom_gerant || '',
      prenom_gerant: souscription.prenom_gerant || '',
      adresse_entreprise: souscription.adresse_entreprise || '',
      code_postal: souscription.code_postal || '',
      ville: souscription.ville || '',
      telephone: souscription.telephone || '',
      email: souscription.email || '',
      chiffre_affaires: souscription.chiffre_affaires || 0,
      montant_ht_contribution: souscription.montant_ht_contribution || 0,
      montant_total: souscription.montant_total || 0,
      effectif: souscription.effectif || '',
      date_debut: souscription.date_debut ? String(souscription.date_debut).split('T')[0] : '',
      date_fin: souscription.date_fin ? String(souscription.date_fin).split('T')[0] : '',
      notes: souscription.notes || '',
      activites_selectionnees: souscription.activites_selectionnees || {}
    });
  };

  const handleEditSubmit = async () => {
    if (!canModify) {
      toast.error('Cette souscription ne peut pas être modifiée');
      return;
    }
    
    setLoading(true);
    try {
      // Utiliser l'ID ou le numéro de police
      const souscriptionId = souscription.id || souscription.numero_police;
      // Normalisation : dates -> ISO, on omet les champs vides pour ne pas écraser
      const payload = { ...editForm };
      ['date_debut', 'date_fin'].forEach((k) => {
        if (payload[k]) payload[k] = `${payload[k]}T00:00:00+00:00`;
        else delete payload[k];
      });
      if (payload.notes === '') delete payload.notes;
      if (payload.effectif === '') delete payload.effectif;
      const _response = await axios.put(
        `${BACKEND_URL}/api/souscriptions/admin/${souscriptionId}/update`,
        payload,
        { headers: getAuthHeader() }
      );
      
      toast.success('Souscription modifiée et documents régénérés');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const _handleAvenantSubmit = async () => {
    if (!canCreateAvenant) {
      toast.error('Impossible de créer un avenant pour cette souscription');
      return;
    }
    
    if (avenantForm.montant_ht_contribution <= 0) {
      toast.error('Le montant de base doit être supérieur à 0');
      return;
    }
    
    setLoading(true);
    try {
      // Utiliser l'ID ou le numéro de police
      const souscriptionId = souscription.id || souscription.numero_police;
      const response = await axios.post(
        `${BACKEND_URL}/api/souscriptions/admin/${souscriptionId}/avenant`,
        avenantForm,
        { headers: getAuthHeader() }
      );
      
      toast.success(`Avenant ${response.data.avenant?.reference} créé avec succès`);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {activeTab === 'modification' ? (
              <>
                <Edit3 className="h-5 w-5 text-blue-600" />
                Modifier la souscription
              </>
            ) : (
              <>
                <FilePlus className="h-5 w-5 text-purple-600" />
                Créer un avenant
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {checkingModifiable ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Vérification des permissions...</span>
          </div>
        ) : (
          <>
            {/* Info contrat */}
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-slate-600" />
                <span className="font-medium">Contrat de référence</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Référence:</span>
                  <span className="ml-2 font-mono font-medium">
                    {souscription?.numero_police || souscription?.ref_solidaire}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Raison sociale:</span>
                  <span className="ml-2 font-medium">{souscription?.raison_sociale}</span>
                </div>
                <div>
                  <span className="text-slate-500">Période:</span>
                  <span className="ml-2">
                    {formatDate(souscription?.periode_adhesion_debut)} - {formatDate(souscription?.periode_adhesion_fin)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Statut:</span>
                  <Badge className={`ml-2 ${
                    souscription?.paiement_valide 
                      ? 'bg-green-600 text-white' 
                      : 'bg-orange-600 text-white'
                  }`}>
                    {souscription?.paiement_valide ? 'Payé' : 'Non payé'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger 
                  value="modification" 
                  disabled={!canModify}
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Modification
                  {!canModify && <AlertCircle className="h-4 w-4 ml-2 text-red-500" />}
                </TabsTrigger>
              </TabsList>

              {/* Tab Modification */}
              <TabsContent value="modification" className="space-y-4 mt-4">
                {!canModify ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Modification impossible</span>
                    </div>
                    <p className="text-sm text-red-600 mt-1">
                      Cette souscription a déjà été payée ou son statut ne permet plus la modification.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 text-blue-700">
                        <Info className="h-5 w-5" />
                        <span className="font-medium">Modification autorisée</span>
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        La souscription n&apos;a pas encore été payée. Vous pouvez modifier les informations ci-dessous.
                      </p>
                    </div>

                    {/* Informations entreprise */}
                    <div className="space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Informations entreprise
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Raison sociale</Label>
                          <Input
                            value={editForm.raison_sociale}
                            onChange={(e) => setEditForm({...editForm, raison_sociale: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>SIRET</Label>
                          <Input
                            value={editForm.siret}
                            onChange={(e) => setEditForm({...editForm, siret: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nom gérant</Label>
                          <Input
                            value={editForm.nom_gerant}
                            onChange={(e) => setEditForm({...editForm, nom_gerant: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Prénom gérant</Label>
                          <Input
                            value={editForm.prenom_gerant}
                            onChange={(e) => setEditForm({...editForm, prenom_gerant: e.target.value})}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Adresse</Label>
                        <Input
                          value={editForm.adresse_entreprise}
                          onChange={(e) => setEditForm({...editForm, adresse_entreprise: e.target.value})}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Code postal</Label>
                          <Input
                            value={editForm.code_postal}
                            onChange={(e) => setEditForm({...editForm, code_postal: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Ville</Label>
                          <Input
                            value={editForm.ville}
                            onChange={(e) => setEditForm({...editForm, ville: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <PhoneInput
                            value={editForm.telephone}
                            onChange={(value) => setEditForm({...editForm, telephone: value})}
                            label="Téléphone"
                          />
                        </div>
                        <div>
                          <EmailInput
                            value={editForm.email}
                            onChange={(value) => setEditForm({...editForm, email: value})}
                            label="Email"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Chiffre d&apos;affaires (€)</Label>
                          <Input
                            type="number"
                            value={editForm.chiffre_affaires || ''}
                            onChange={(e) => setEditForm({...editForm, chiffre_affaires: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Montant de base contribution (€)</Label>
                          <Input
                            type="number"
                            value={editForm.montant_ht_contribution}
                            onChange={(e) => setEditForm({...editForm, montant_ht_contribution: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Montant total TTC (€)</Label>
                          <Input
                            type="number"
                            data-testid="edit-montant-total"
                            value={editForm.montant_total}
                            onChange={(e) => setEditForm({...editForm, montant_total: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Effectif</Label>
                          <Input
                            data-testid="edit-effectif"
                            value={editForm.effectif}
                            onChange={(e) => setEditForm({...editForm, effectif: e.target.value})}
                          />
                        </div>
                      </div>

                      {(liveCalcLoading || liveCalc || liveCalcError) && (
                        <div data-testid="batico-live-calc" className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                              <Info className="h-4 w-4" />
                              Montant estimé recalculé (BATICO)
                            </div>
                            {liveCalcLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                          </div>
                          {liveCalcError && !liveCalcLoading && (
                            <p data-testid="batico-live-error" className="text-xs text-red-600 mt-1">{liveCalcError}</p>
                          )}
                          {liveCalc && !liveCalcLoading && (
                            <div className="mt-2 flex items-end justify-between gap-4">
                              <div className="text-sm text-slate-700">
                                <div>HT : <span className="font-semibold" data-testid="batico-live-ht">{(liveCalc.montant_ht ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                                <div>TTC : <span className="font-semibold text-blue-700" data-testid="batico-live-ttc">{(liveCalc.montant_ttc ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span></div>
                                {liveCalc.plancher?.applique && (
                                  <div className="text-xs text-amber-700 mt-1">Cotisation au plancher contributif</div>
                                )}
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                data-testid="batico-apply-btn"
                                onClick={() => setEditForm((prev) => ({
                                  ...prev,
                                  montant_ht_contribution: liveCalc.montant_ht ?? prev.montant_ht_contribution,
                                  montant_total: liveCalc.montant_ttc ?? prev.montant_total,
                                }))}
                              >
                                Appliquer au montant
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      <h4 className="font-medium flex items-center gap-2 pt-2">
                        <CalendarDays className="h-4 w-4" />
                        Période de couverture
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Date de début</Label>
                          <DatePickerFr
                            className="w-full"
                            value={editForm.date_debut}
                            onChange={(v) => setEditForm({...editForm, date_debut: v})}
                          />
                        </div>
                        <div>
                          <Label>Date de fin</Label>
                          <DatePickerFr
                            className="w-full"
                            value={editForm.date_fin}
                            onChange={(v) => setEditForm({...editForm, date_fin: v})}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Observations / mentions libres (attestation)</Label>
                        <Textarea
                          data-testid="edit-notes-libre"
                          rows={4}
                          placeholder="Texte libre ajouté au document (mentions particulières, remarques…)"
                          value={editForm.notes}
                          onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Ce texte et les champs ci-dessus régénèrent l&apos;attestation et les CP/CG à l&apos;enregistrement.
                        </p>
                      </div>

                      <div className="pt-2">
                        <h4 className="font-medium mb-2">Activités couvertes</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[
                            { cat: 'gros_oeuvre', key: 'gros_oeuvre', label: 'Gros Œuvre' },
                            { cat: 'second_oeuvre', key: 'second_oeuvre', label: 'Second Œuvre' },
                            { cat: 'travaux_publics', key: 'travaux_publics', label: 'Travaux Publics' },
                            { cat: 'intellectuels', key: 'pib', label: 'Professionnels Intellectuels (PIB)' },
                          ].map(({ cat, key, label }) => (
                            <SelecteurActivites
                              key={key}
                              categorie={cat}
                              label={label}
                              selectedIds={editForm.activites_selectionnees?.[key] || []}
                              onChange={(ids) => setEditForm((prev) => ({
                                ...prev,
                                activites_selectionnees: { ...(prev.activites_selectionnees || {}), [key]: ids }
                              }))}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Annuler
              </Button>
              
              {activeTab === 'modification' && canModify && (
                <Button 
                  onClick={handleEditSubmit} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer les modifications
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
